// src/hooks/useOrganization.js
import { useAuth } from '../context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../config/supabase';

export function useOrganization() {
  const { user, organizationId, permissions } = useAuth();

  // Organization data query
  const { 
    data: organizationData,
    isLoading: loadingOrg 
  } = useQuery({
    queryKey: ['organization', organizationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data, error } = await supabase
        .from('organizations')
        .select(`
          *,
          domains (
            id,
            domain_name,
            status
          ),
          organization_members (
            id,
            user_id,
            role
          )
        `)
        .eq('id', organizationId)
        .single();

      if (error) {
        console.error('Error fetching organization:', error);
        throw error;
      }
      return data;
    },
    enabled: !!organizationId,
  });

  // Organization members query
  const { 
    data: members,
    isLoading: loadingMembers 
  } = useQuery({
    queryKey: ['organizationMembers', organizationId],
    queryFn: async () => {
      if (!organizationId || !permissions?.canViewOrgData) return [];

      const { data, error } = await supabase
        .from('organization_members')
        .select(`
          id,
          role,
          users (
            id,
            email,
            name
          )
        `)
        .eq('organization_id', organizationId);

      if (error) {
        console.error('Error fetching members:', error);
        throw error;
      }
      return data;
    },
    enabled: !!organizationId && !!permissions?.canViewOrgData,
  });

  // Organization metrics query
  const { 
    data: metrics,
    isLoading: loadingMetrics 
  } = useQuery({
    queryKey: ['organizationMetrics', organizationId],
    queryFn: async () => {
      if (!organizationId || !permissions?.canViewAnalytics) return null;

      const { data, error } = await supabase
        .from('organization_analytics')
        .select('*')
        .eq('organization_id', organizationId)
        .order('date', { ascending: false })
        .limit(30);

      if (error) {
        console.error('Error fetching metrics:', error);
        throw error;
      }
      return data;
    },
    enabled: !!organizationId && !!permissions?.canViewAnalytics,
    refetchInterval: 30000,
  });

  // User metrics query
  const { 
    data: userMetrics,
    isLoading: loadingUserMetrics 
  } = useQuery({
    queryKey: ['userMetrics', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('processed_emails')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching user metrics:', error);
        throw error;
      }
      return data;
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
  });

  // Debug logging (can be removed in production)
  console.log('Organization Context:', {
    organizationId,
    hasPermissions: !!permissions,
    canViewOrgData: permissions?.canViewOrgData,
    canViewAnalytics: permissions?.canViewAnalytics,
    isAdmin: permissions?.isAdmin
  });

  return {
    // Organization data
    organization: organizationData,
    organizationId,
    members,
    metrics,
    userMetrics,

    // Loading states
    isLoading: loadingOrg || loadingMembers || loadingMetrics || loadingUserMetrics,
    loadingOrg,
    loadingMembers,
    loadingMetrics,
    loadingUserMetrics,

    // Permissions
    canViewOrgData: permissions?.canViewOrgData || false,
    canManageUsers: permissions?.canManageUsers || false,
    canViewAnalytics: permissions?.canViewAnalytics || false,
    isAdmin: permissions?.isAdmin || false,
    isDomainAdmin: permissions?.isDomainAdmin || false,

    // Organization status
    isConfigured: organizationData?.email_provider_configured || false,
    settings: organizationData?.settings || {},

    // Computed properties
    hasMembers: Array.isArray(members) && members.length > 0,
    hasMetrics: Array.isArray(metrics) && metrics.length > 0,
    totalMembers: Array.isArray(members) ? members.length : 0
  };
}